/**
 * Home › Projects (user decision 2026-10-08): variant B, card-lite — two lines per project:
 *   1. name (+ "Blocked · reason") and the counts in fixed columns: ⬣ blocked · ◆ needs you · running
 *   2. the latest meaningful progress (status icon, text, age) and freshness
 * with variant A, a dense table, as a density toggle (`t`). Blocked / needs-you projects first,
 * then active ones; quiet projects fold into one "N quiet projects" line. Every number drills
 * (to Home › Needs you or Running for that project); counts never truncate.
 */
import { Link } from '@tanstack/react-router'
import { StatusIcon } from '@viko/transcript-ui'
import { ChevronRight, Clock } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { homeHref, projectHref } from '../../lib/ia.js'
import { blockedReason, isBlockedRow } from '../../lib/needs-you.js'
import {
  groupProjects,
  type ProjectDensity,
  type ProjectRow,
  projectRow,
  quietWhat,
  rowIcon,
  saveDensity,
  storedDensity,
} from '../../lib/project-list.js'
import { isTyping } from '../../lib/shortcut.js'
import { useInboxRows } from '../inbox/useInboxRows.js'
import { useTabOrder } from '../inbox/useTabOrder.js'
import { Ago, Empty, SkeletonRows } from '../ui.js'
import { useOverview } from './Overview.js'
import './project-list.css'

export function ProjectList() {
  const { overview, probes, needs, pending } = useOverview()
  const built = useInboxRows()
  const tabs = useTabOrder()
  const [density, setDensity] = useState<ProjectDensity>(storedDensity)
  const [quietOpen, setQuietOpen] = useState(false)
  const set = (d: ProjectDensity) => {
    setDensity(d)
    saveDensity(d)
  }
  // `t` toggles the density (never while typing or in a dialog).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 't' || e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      e.preventDefault()
      setDensity((d) => {
        const next = d === 'rows' ? 'table' : 'rows'
        saveDensity(next)
        return next
      })
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [])

  const groups = useMemo(() => {
    const reasons = new Map<string, string>()
    for (const r of built.rows)
      if (r.blocking && r.projectId && isBlockedRow(r) && !reasons.has(r.projectId))
        reasons.set(r.projectId, blockedReason(r))
    const rows = overview.tiles.map((t) =>
      projectRow(
        t,
        { blocked: needs.blockedOf(t.projectId), needs: needs.decideOf(t.projectId) },
        reasons.get(t.projectId),
      ),
    )
    return groupProjects(rows)
  }, [overview.tiles, needs, built.rows])

  if (pending) return <SkeletonRows n={4} />
  if (!overview.tiles.length)
    return (
      <Empty center title="No projects yet">
        Add a project to see its status here.
      </Empty>
    )

  const sections: { key: string; title: string; rows: ProjectRow[] }[] = [
    { key: 'attention', title: 'Needs attention', rows: groups.attention },
    { key: 'active', title: 'Active', rows: groups.active },
  ]
  const quietLine = groups.quiet.length ? (
    <button
      type="button"
      className="pl-quiet"
      aria-expanded={quietOpen}
      aria-label={`${groups.quiet.length} quiet ${groups.quiet.length === 1 ? 'project' : 'projects'}`}
      onClick={() => setQuietOpen(!quietOpen)}
    >
      <span className="pl-quiet__tw vk-icol">
        <ChevronRight size={16} aria-hidden="true" className="pl-chev" />
      </span>
      <b>Quiet</b>
      <span className="pl-quiet__n">{groups.quiet.length}</span>
      {quietOpen ? null : (
        <span className="pl-quiet__list" aria-hidden="true">
          {groups.quiet.map((r, i) => {
            const q = quietWhat(r)
            return (
              <span key={r.projectId}>
                {i ? <span className="pl-dot"> · </span> : null}
                {q.name}{' '}
                <span className="pl-t3">
                  {q.text}
                  {q.since ? (
                    <>
                      {' '}
                      <Ago ts={q.since} />
                    </>
                  ) : null}
                </span>
              </span>
            )
          })}
        </span>
      )}
    </button>
  ) : null
  const total = overview.tiles.length

  return (
    <div className={`pl is-${density}`} data-density={density}>
      <div className="pl-bar">
        <span className="pl-order">
          {total} {total === 1 ? 'project' : 'projects'}
          <span className="pl-dot"> · </span>
          sorted: blocked and needs you, then active, then quiet
        </span>
        <fieldset className="pl-density">
          <legend className="sr-only">Density</legend>
          {(['rows', 'table'] as const).map((d) => (
            <label key={d}>
              <input
                type="radio"
                name="pl-density"
                checked={density === d}
                onChange={() => set(d)}
              />
              <span>{d === 'rows' ? 'Rows' : 'Table'}</span>
            </label>
          ))}
          <span aria-hidden="true">
            <kbd className="ak">t</kbd>
          </span>
        </fieldset>
      </div>
      {density === 'table' ? (
        <ProjectTable
          sections={sections}
          quiet={groups.quiet}
          quietOpen={quietOpen}
          quietLine={quietLine}
          tab={tabs.label}
        />
      ) : (
        <>
          {sections.map((s) =>
            s.rows.length ? (
              <section key={s.key} className="pl-group" aria-labelledby={`pl-${s.key}`}>
                <GroupHead id={`pl-${s.key}`} title={s.title} n={s.rows.length} />
                <ul className="pl-list">
                  {s.rows.map((r) => (
                    <ProjectCard key={r.projectId} row={r} />
                  ))}
                </ul>
              </section>
            ) : null,
          )}
          {quietLine ? (
            <section className="pl-group" aria-label="Quiet projects">
              {quietLine}
              {quietOpen ? (
                <ul className="pl-list">
                  {groups.quiet.map((r) => (
                    <ProjectCard key={r.projectId} row={r} />
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}
        </>
      )}
      {probes}
    </div>
  )
}

function GroupHead({ id, title, n }: { id: string; title: string; n: number }) {
  return (
    <h3 id={id} className="pl-h">
      <span>{title}</span>
      <span className="pl-h__n">{n}</span>
    </h3>
  )
}

/** The three fixed count columns (blocked · needs you · running); zero shows as an empty cell. */
function Counts({ row }: { row: ProjectRow }) {
  return (
    <span className="pl-counts">
      <CountCell
        n={row.blocked}
        kind="blocked"
        href={homeHref({ v: 'needs', project: row.projectId })}
        label={`${row.blocked} blocked in ${row.name}`}
      />
      <CountCell
        n={row.needs}
        kind="wait"
        href={homeHref({ v: 'needs', project: row.projectId })}
        label={`${row.needs} need you in ${row.name}`}
      />
      <CountCell
        n={row.running}
        kind="run"
        href={homeHref({ v: 'running', project: row.projectId })}
        label={`${row.running} running in ${row.name}`}
      />
    </span>
  )
}

function CountCell({
  n,
  kind,
  href,
  label,
}: {
  n: number
  kind: 'blocked' | 'wait' | 'run'
  href: string
  label: string
}) {
  if (!n) return <span className="pl-count is-empty" aria-hidden="true" />
  return (
    <Link to={href} className={`pl-count is-${kind}`} aria-label={label} title={label}>
      <StatusIcon kind={kind} live={false} />
      <span>{n}</span>
    </Link>
  )
}

function Progress({ row }: { row: ProjectRow }) {
  return (
    <span className="pl-progress">
      <span className="vk-icol">
        <StatusIcon kind={row.progress.kind} live={false} />
      </span>
      <span className="pl-progress__t">{row.progress.text}</span>
      {row.progress.at ? <Ago ts={row.progress.at} /> : null}
    </span>
  )
}

/** Shown only when the machine data behind the row is stale (current data needs no word). */
function Freshness({ row }: { row: ProjectRow }) {
  return row.freshness.stale ? (
    <span className="pl-fresh is-stale" title={`Machine data is stale: ${row.freshness.text}`}>
      <Clock size={14} aria-hidden="true" />
      {row.freshness.text}
    </span>
  ) : null
}

function ProjectCard({ row }: { row: ProjectRow }) {
  const icon = rowIcon(row)
  return (
    <li
      className={`pl-row${row.blocked ? ' is-blocked' : row.needs ? ' is-needs' : ''}`}
      data-project={row.projectId}
    >
      <span className="pl-g vk-icol">
        <StatusIcon kind={icon} live={false} />
      </span>
      <span className="pl-nm">
        <Link to={projectHref(row.projectId)} className="pl-name">
          {row.name}
        </Link>
        {row.blocked ? (
          <span className="pl-reason">
            <b>Blocked</b> · {row.blockedReason ?? 'needs a fix'}
          </span>
        ) : null}
      </span>
      <Counts row={row} />
      <span className="pl-row__l2">
        <Progress row={row} />
        {row.freshness.stale ? <span className="pl-dot"> · </span> : null}
        <Freshness row={row} />
      </span>
      {row.blocked ? (
        // Phone (po-b m): a blocked row's second line says why, and how fresh that is.
        <span className="pl-mreason">
          <b>Blocked</b> · {row.blockedReason ?? 'needs a fix'}
          {row.freshness.stale ? (
            <span className="pl-mreason__fresh"> · {row.freshness.text}</span>
          ) : null}
        </span>
      ) : null}
    </li>
  )
}

function ProjectTable({
  sections,
  quiet,
  quietOpen,
  quietLine,
  tab,
}: {
  sections: { key: string; title: string; rows: ProjectRow[] }[]
  quiet: ProjectRow[]
  quietOpen: boolean
  quietLine: ReactNode
  tab: (id?: string) => string | undefined
}) {
  // The Freshness column appears only when some machine data is stale.
  const fresh = [...sections.flatMap((s) => s.rows), ...quiet].some((r) => r.freshness.stale)
  const cols = fresh ? 7 : 6
  const body = (rows: ProjectRow[]) =>
    rows.map((r) => (
      <tr
        key={r.projectId}
        className={r.blocked ? 'is-blocked' : r.needs ? 'is-needs' : ''}
        data-project={r.projectId}
      >
        <td className="pl-t__key">
          {tab(r.projectId) ? <kbd className="ak">{tab(r.projectId)}</kbd> : null}
        </td>
        <th
          scope="row"
          className="pl-t__name"
          title={r.blocked ? `Blocked · ${r.blockedReason ?? 'needs a fix'}` : undefined}
        >
          <Link to={projectHref(r.projectId)} className="pl-name">
            {r.name}
          </Link>
          {r.blocked ? (
            <span className="sr-only"> Blocked · {r.blockedReason ?? 'needs a fix'}</span>
          ) : null}
        </th>
        <td className="pl-t__n">
          <CountCell
            n={r.blocked}
            kind="blocked"
            href={homeHref({ v: 'needs', project: r.projectId })}
            label={`${r.blocked} blocked in ${r.name}`}
          />
        </td>
        <td className="pl-t__n">
          <CountCell
            n={r.needs}
            kind="wait"
            href={homeHref({ v: 'needs', project: r.projectId })}
            label={`${r.needs} need you in ${r.name}`}
          />
        </td>
        <td className="pl-t__n">
          <CountCell
            n={r.running}
            kind="run"
            href={homeHref({ v: 'running', project: r.projectId })}
            label={`${r.running} running in ${r.name}`}
          />
        </td>
        <td className="pl-t__p">
          <Progress row={r} />
        </td>
        {fresh ? (
          <td className="pl-t__f">
            <Freshness row={r} />
          </td>
        ) : null}
      </tr>
    ))
  return (
    <div className="pl-t__wrap">
      <table className="pl-t">
        <thead>
          <tr>
            <th scope="col" className="pl-t__key">
              <span className="sr-only">Key</span>
            </th>
            <th scope="col" className="pl-t__name">
              Project
            </th>
            <th scope="col" className="pl-t__n">
              Blocked
            </th>
            <th scope="col" className="pl-t__n">
              Needs you
            </th>
            <th scope="col" className="pl-t__n">
              Running
            </th>
            <th scope="col" className="pl-t__p">
              Latest progress
            </th>
            {fresh ? (
              <th scope="col" className="pl-t__f">
                Freshness
              </th>
            ) : null}
          </tr>
        </thead>
        {sections.map((s) =>
          s.rows.length ? (
            <tbody key={s.key}>
              <tr className="pl-t__group">
                <th scope="colgroup" colSpan={cols}>
                  {s.title} <span className="pl-h__n">{s.rows.length}</span>
                </th>
              </tr>
              {body(s.rows)}
            </tbody>
          ) : null,
        )}
        {quiet.length ? (
          <tbody>
            <tr className="pl-t__group">
              <td colSpan={cols}>{quietLine}</td>
            </tr>
            {quietOpen ? body(quiet) : null}
          </tbody>
        ) : null}
      </table>
    </div>
  )
}
